import React, { useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { chatCreateTemplateApi } from "../../api/organizerChatApi";
import { CAMPOS_WHATSAPP, parametrosPorDefecto } from "./recordatorios";
import {
  MAX_CUERPO,
  MAX_PIE,
  NOMBRE_VALIDO,
  cantidadDeVariables,
  mensajeDeChat,
  textoDeVistaPrevia,
} from "./plantillasWhatsapp";
import VistaPreviaWhatsapp from "./VistaPreviaWhatsapp";
import "./recordatorios.css";

/* Create-template modal of the reminder settings. The back fixes the language (es_AR); the category is utility unless
   the caller passes `categoria="MARKETING"` (client campaigns);
   the venue only writes the name, the message with its numbered variables and an optional footer. */

const IconoMas = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const IconoInfo = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M12 16v-4M12 8h.01" />
  </svg>
);

const marca = (n) => `{{${n}}}`;

/** `onCreada({ name, status, parametros })` runs once Chat accepted the template; the caller picks it. */
const PlantillaNuevaModal = ({ workspaceId, workspaceNombre, local, onClose, onCreada, campos = CAMPOS_WHATSAPP, categoria = "UTILITY" }) => {
  const marketing = categoria === "MARKETING";
  const [nombre, setNombre] = useState("");
  const [cuerpo, setCuerpo] = useState("");
  const [pie, setPie] = useState("");
  // Field each inserted variable stands for (by number), so the preview and the default mapping know what it is.
  const [asignados, setAsignados] = useState({});
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const areaRef = useRef(null);

  const cantidad = cantidadDeVariables(cuerpo);
  const parametros = useMemo(() => {
    const porDefecto = parametrosPorDefecto(cantidad, campos);
    return Array.from({ length: cantidad }, (_, i) => asignados[i + 1] || porDefecto[i] || "");
  }, [asignados, cantidad, campos]);
  // field -> number of its variable, only while that variable is still in the text
  const numeroDe = useMemo(() => {
    const mapa = {};
    for (let n = 1; n <= cantidad; n += 1) if (asignados[n] && cuerpo.includes(marca(n))) mapa[asignados[n]] = n;
    return mapa;
  }, [asignados, cantidad, cuerpo]);

  const insertar = (campo) => {
    if (numeroDe[campo]) return;
    const n = cantidad + 1;
    const area = areaRef.current;
    const desde = area?.selectionStart ?? cuerpo.length;
    const hasta = area?.selectionEnd ?? cuerpo.length;
    const nuevo = `${cuerpo.slice(0, desde)}${marca(n)}${cuerpo.slice(hasta)}`;
    if (nuevo.length > MAX_CUERPO) return;
    setCuerpo(nuevo);
    setAsignados((prev) => ({ ...prev, [n]: campo }));
  };

  const enviar = async () => {
    const name = nombre.trim();
    if (!name || !NOMBRE_VALIDO.test(name)) {
      setError("El nombre solo puede tener minúsculas, números y guion bajo.");
      return;
    }
    if (!cuerpo.trim()) {
      setError("Escribí el mensaje de la plantilla.");
      return;
    }
    setError("");
    setEnviando(true);
    try {
      const res = await chatCreateTemplateApi(workspaceId, { name, body: cuerpo, footer: pie.trim(), ...(marketing ? { category: "MARKETING" } : {}) });
      onCreada?.({ name, status: res?.data?.info?.status || "PENDING", parametros });
      onClose();
    } catch (e) {
      setError(mensajeDeChat(e, "No pudimos crear la plantilla. Probá de nuevo."));
      setEnviando(false);
    }
  };

  return createPortal(
    <div className="zr-overlay" onMouseDown={enviando ? undefined : onClose}>
      <div
        className="zr-modal zr-side-panel zr-srv-modal rwa-modal--ancho"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pl-titulo"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="rwa-modal-head">
          <div>
            <h3 id="pl-titulo" className="zr-side-panel__title">Nueva plantilla de WhatsApp</h3>
            <p className="zr-section-head__hint">Se crea en el workspace {workspaceNombre}.</p>
          </div>
          <button type="button" className="zr-btn zr-btn--ghost zr-side-panel__close" onClick={onClose} disabled={enviando} aria-label="Cerrar">
            ×
          </button>
        </div>

        <div className="rwa-modal-cols">
          <div className="pu-body">
            {error && <div className="ef-alert ef-alert--danger" role="alert">{error}</div>}

            <div className="ef-field">
              <label className="ef-label" htmlFor="pl-nombre">Nombre</label>
              <input
                id="pl-nombre"
                className="ef-input"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="recordatorio_turno"
                autoComplete="off"
              />
              <p className="ef-footnote">Minúsculas, números y guion bajo. Sin espacios ni acentos.</p>
            </div>

            <div className="pu-grid-2">
              <div className="ef-field">
                <label className="ef-label" htmlFor="pl-idioma">Idioma</label>
                <input id="pl-idioma" className="rwa-fijo" value="Español (Argentina)" readOnly />
              </div>
              <div className="ef-field">
                <label className="ef-label" htmlFor="pl-categoria">Categoría</label>
                <input id="pl-categoria" className="rwa-fijo" value={marketing ? "Marketing" : "Utilidad"} readOnly />
                <p className="ef-footnote">
                  {marketing
                    ? "Un saludo o una invitación a volver es un mensaje de marketing: Meta lo trata como promoción."
                    : "Un recordatorio de turno avisa algo que el cliente ya reservó: es un mensaje de utilidad, no una promoción."}
                </p>
              </div>
            </div>

            <div className="ef-field">
              <label className="ef-label" htmlFor="pl-mensaje">Mensaje</label>
              <textarea
                id="pl-mensaje"
                ref={areaRef}
                className="ef-textarea"
                style={{ height: 150 }}
                value={cuerpo}
                maxLength={MAX_CUERPO}
                onChange={(e) => setCuerpo(e.target.value)}
                placeholder="Hola {{1}}, te recordamos tu turno en {{2}}…"
              />
              <div className="rwa-contador">
                <p className="ef-footnote">Sumá datos que cambian en cada turno con los botones de abajo.</p>
                <span className="fc-counter">{cuerpo.length} / {MAX_CUERPO}</span>
              </div>
            </div>

            <div>
              <span id="pl-chips-label" className="rwa-chips-label">Insertar dato</span>
              <div className="rwa-chips" role="group" aria-labelledby="pl-chips-label">
                {campos.map((campo) => {
                  const n = numeroDe[campo.value];
                  return (
                    <button
                      key={campo.value}
                      type="button"
                      className={`rwa-chip${n ? " rwa-chip--usado" : ""}`}
                      onClick={() => insertar(campo.value)}
                      aria-pressed={Boolean(n)}
                    >
                      {n ? null : <IconoMas />}
                      {campo.label}
                      {n ? <span className="rwa-chip__n">{marca(n)}</span> : null}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="ef-field">
              <label className="ef-label" htmlFor="pl-pie">Pie (opcional)</label>
              <input
                id="pl-pie"
                className="ef-input"
                value={pie}
                maxLength={MAX_PIE}
                onChange={(e) => setPie(e.target.value)}
                autoComplete="off"
              />
              <div className="rwa-contador">
                <p className="ef-footnote">Va en gris debajo del mensaje y no admite datos variables.</p>
                <span className="fc-counter">{pie.length} / {MAX_PIE}</span>
              </div>
            </div>
          </div>

          <VistaPreviaWhatsapp
            local={local}
            texto={textoDeVistaPrevia(cuerpo || "Tu mensaje aparece acá.", parametros, local)}
            pie={pie.trim()}
          />
        </div>

        <div className="zr-info rwa-nota" role="note">
          <IconoInfo />
          <span>Meta revisa cada plantilla antes de poder usarla. Suele tardar de unos minutos a 24 h.</span>
        </div>

        <div className="zr-side-panel__foot">
          <button type="button" className="zr-btn zr-btn--ghost" onClick={onClose} disabled={enviando}>Cancelar</button>
          <button type="button" className="zr-btn zr-btn--dark" onClick={enviar} disabled={enviando}>
            {enviando ? "Enviando…" : "Enviar a aprobación"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default PlantillaNuevaModal;
